/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import Placeholder from 'src/components/Dashboard/Placeholder';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';
import { PortfolioShow } from 'src/components/Portfolio';

import portfolioShowParams from './helpers/portfolio';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

export const portfolioShowRouteQuery = graphql`
  query PortfolioShowRoute_Query(
    $uuid: ID!
  ) {
      ...PortfolioShow
        @arguments(
          uuid: $uuid
        )
  }
`;

const portfolioShowRoutePropsShape = PropTypes.shape({
  match: PropTypes.shape({
    location: PropTypes.shape({
      query: PropTypes.shape({
        aggregateBy: PropTypes.string,
        groupBy: PropTypes.string,
        source: PropTypes.string,
        tz: PropTypes.string,
        unit: PropTypes.string,
        xAxisDomain: PropTypes.string,
      }),
    }),
    params: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
    }),
  }).isRequired,
});

const queryParamsPropShape = PropTypes.shape({
  aggregateBy: PropTypes.string,
  groupBy: PropTypes.string,
  source: PropTypes.string,
  timezone: PropTypes.string,
  unit: PropTypes.string,
  xAxisDomain: PropTypes.string,
});

/**
 * Renders `PortfolioShow` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @param {object} props.queryParams - Portfolio view params derived from the route query.
 * @returns {React.ReactNode} portfolio show route content.
 */
function PortfolioShowPreloadedQueryRenderer({
  routeProps,
  queryRef,
  queryParams,
}) {
  const queryData = usePreloadedQuery(portfolioShowRouteQuery, queryRef);

  return (
    <PortfolioShow
      {...routeProps}
      {...queryData}
      queryParams={queryParams}
    />
  );
}

PortfolioShowPreloadedQueryRenderer.propTypes = {
  routeProps: portfolioShowRoutePropsShape.isRequired,
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
  queryParams: queryParamsPropShape.isRequired,
};

/**
 * Hook-safe renderer for the `PortfolioShow` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state.
 * Caller MUST mount this with `key={routeProps.match.params.uuid}`
 * so a uuid change disposes the stale `useQueryLoader` query .
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @returns {React.ReactNode} portfolio show route content.
 */
function PortfolioShowRouteRenderer({ routeProps }) {
  const queryParams = portfolioShowParams(routeProps);
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    portfolioShowRouteQuery,
    routeProps,
    { logLabel: 'PortfolioShowRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      placeholderComponent={Placeholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {queryRef ? (
          <PortfolioShowPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
            queryParams={queryParams}
          />
        ) : (
          <PortfolioShow
            {...routeProps}
            queryParams={queryParams}
          />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

PortfolioShowRouteRenderer.propTypes = {
  routeProps: portfolioShowRoutePropsShape.isRequired,
};

export default PortfolioShowRouteRenderer;
